import type { IncomingInvoiceReviewFormSchema } from "@msuite/utils/schemas";
import { allocationCents } from "@/forms/incoming-invoice-cost-allocation-form";
import { getReviewNetAmount } from "@/forms/incoming-invoice-review-form";

/** Why an acceptance would be refused, and the field it belongs to. */
export type AcceptanceError =
	| { field: "reducedNetAmount"; kind: "reduction-too-high" }
	| { field: "root"; kind: "allocation-mismatch"; netAmount: number };

/**
 * What the server would refuse about an acceptance, found before the round trip so the modal
 * stays open with the rows as they are: a Kürzung that does not lower the printed net amount, and
 * a cost allocation that misses the payable net amount by a cent or more. An empty allocation is
 * valid — it clears the split.
 *
 * @param values - The submitted review form.
 * @param printedNetAmount - The net amount read off the invoice.
 * @param hasCostCenters - False when the allocation is sent empty anyway.
 * @returns The first error, or null when the acceptance can be sent.
 */
export function findAcceptanceError(
	values: IncomingInvoiceReviewFormSchema,
	printedNetAmount: number | null,
	hasCostCenters: boolean,
): AcceptanceError | null {
	if (values.state !== "approved") return null;
	const reduced = values.reducedNetAmount;
	if (
		values.isReduced &&
		printedNetAmount !== null &&
		reduced !== null &&
		reduced >= printedNetAmount
	) {
		return { field: "reducedNetAmount", kind: "reduction-too-high" };
	}
	if (!hasCostCenters || !values.allocations.length) return null;
	const netAmount = getReviewNetAmount(printedNetAmount, values);
	const cents = allocationCents(values.allocations.map((allocation) => allocation.amount));
	if (cents === Math.round(netAmount * 100)) return null;
	return { field: "root", kind: "allocation-mismatch", netAmount };
}
